import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, BatteryChargingIcon, MagnetIcon, PaletteIcon, RulerIcon, SparklesIcon, WifiOffIcon } from 'lucide-react';
import { ProductAssetSlot } from '../product/ProductAssetSlot';
import { CountUp } from '../fx/CountUp';
import { productFeatures } from '../../data/productFeatures';
import type { ProductFeature } from '../../data/productFeatures';
const ICONS: Record<ProductFeature['icon'], React.ElementType> = {
  wifi: WifiOffIcon,
  battery: BatteryChargingIcon,
  palette: PaletteIcon,
  ruler: RulerIcon,
  magnet: MagnetIcon,
  sparkles: SparklesIcon
};
const CYCLE_MS = 3600;
const ease = [0.23, 1, 0.32, 1] as const;

/** Interactive product console: feature list ↔ orbit nodes ↔ live readout, auto-cycling until touched. */
export function ProductReveal() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (touched || reduce) return;
    const id = window.setInterval(() => setActive(a => (a + 1) % productFeatures.length), CYCLE_MS);
    return () => window.clearInterval(id);
  }, [touched, reduce]);
  const select = (i: number) => {
    setTouched(true);
    setActive(i);
  };
  const f = productFeatures[active];
  const left = productFeatures.slice(0, 3);
  const right = productFeatures.slice(3);
  return <section aria-labelledby="reveal-title" className="mf-tech-grid relative overflow-hidden bg-deep" data-id="element-169" data-mp="11vn8iex8b2xu">
      <div className="relative mx-auto max-w-site px-5 pb-24 pt-14 md:px-8 md:pb-32 md:pt-20" data-id="element-170" data-mp="16mvhxayk6ob0">
        <div className="text-center" data-id="element-171" data-mp="1bmhwbnq4iale">
          <p className="font-display text-sm uppercase tracking-[0.3em] text-marine" data-id="element-172" data-mp="1blghd85zl3ye">System · M1 PRO</p>
          <h2 id="reveal-title" className="mt-3 font-display text-6xl font-semibold uppercase leading-[0.9] md:text-8xl" data-id="element-173" data-mp="1gn5ssox4fjn0">
            Meet the M1 PRO.
          </h2>
          <p className="mt-4 font-display text-xl uppercase tracking-[0.14em] text-ice md:text-2xl" data-id="element-174" data-mp="vxbpu9wnwrv5">
            Professional-Grade Lighting. Reimagined.
          </p>
        </div>

        <div className="mt-14 grid items-center gap-10 lg:grid-cols-[1fr_1.35fr_1fr] lg:gap-12" data-id="element-175" data-mp="15mlf7i04fwfk">
          <FeatureList items={left} offset={0} active={active} onSelect={select} align="right" data-id="element-176" data-mp="10utsxjo00cv8" />

          {/* Product console */}
          <div className="relative mx-auto aspect-square w-full max-w-[540px]" data-id="element-177" data-mp="1el6wzaxrr7fa">
            <OrbitRings data-id="element-178" data-mp="19mnb1aj6dvmx" />
            {productFeatures.map((pf, i) => {
            const angle = i / productFeatures.length * Math.PI * 2 - Math.PI / 2;
            const x = 50 + Math.cos(angle) * 47;
            const y = 50 + Math.sin(angle) * 47;
            const on = i === active;
            const Icon = ICONS[pf.icon];
            return <button key={pf.id} type="button" onClick={() => select(i)} aria-label={pf.title} aria-pressed={on} className={`absolute z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border transition-[background-color,border-color,color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice ${on ? 'scale-110 border-ice bg-marine text-white shadow-[0_0_18px_rgba(36,141,255,0.7)]' : 'border-marine/40 bg-deep text-titanium hover:border-ice hover:text-white'}`} style={{
              left: `${x}%`,
              top: `${y}%`
            }} data-id="element-179" data-mp="3ldhg61hmlp7k" data-mpi={i}>
                  <Icon className="h-5 w-5" aria-hidden data-id="element-180" data-mp="32kj3pk8lrr6c" />
                </button>;
          })}
            <div className="absolute inset-[14%] overflow-hidden rounded-full" data-id="element-181" data-mp="37jdjedb9wzm8">
              <ProductAssetSlot view="lit" tone="studio" className="h-full w-full !rounded-full !border-marine/30" data-id="element-182" data-mp="37h9im48o8gq6" />
              <span className="mf-scan" aria-hidden data-id="element-183" data-mp="12ijactktojgf" />
            </div>
          </div>

          <FeatureList items={right} offset={3} active={active} onSelect={select} align="left" data-id="element-184" data-mp="ygo9trapua3p" />
        </div>

        {/* Live readout */}
        <div className="mf-hud mx-auto mt-12 max-w-3xl border border-marine/25 bg-[#06121F]/80 px-6 py-6 backdrop-blur-sm md:px-10" aria-live="polite" data-id="element-185" data-mp="tgjrrwu5t7bd">
          <AnimatePresence mode="wait" initial={false} data-id="element-186" data-mp="okcxj1b4lk99">
            <motion.div key={f.id} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.2,
            ease
          }} className="grid items-center gap-4 text-center md:grid-cols-[auto_1fr] md:gap-10 md:text-left" data-id="element-187" data-mp="jr084oymh9fg">
              <div className="font-display uppercase" data-id="element-188" data-mp="t7g4hpjejwu">
                <span className="text-6xl font-semibold leading-none text-white md:text-7xl" data-id="element-189" data-mp="tpky2q9m6gd">
                  <CountUp value={f.metric.value} decimals={f.metric.decimals} data-id="element-190" data-mp="2cfrcy1p8a65c" />
                </span>
                <span className="ml-2 text-2xl font-semibold tracking-[0.06em] text-ice" data-id="element-191" data-mp="2hdj60z1lzyb4">{f.metric.unit}</span>
                <p className="mt-1 text-sm tracking-[0.16em] text-titanium" data-id="element-192" data-mp="2mcckatopztcc">{f.metric.label}</p>
              </div>
              <div data-id="element-193" data-mp="2qdpoz0g2tqho">
                <h3 className="font-display text-2xl font-semibold uppercase tracking-[0.04em]" data-id="element-194" data-mp="1tqbdlhpa4jqi">{f.title}</h3>
                <p className="mt-1 text-titanium" data-id="element-195" data-mp="1yo34q9i3o1tn">{f.body}</p>
              </div>
            </motion.div>
          </AnimatePresence>
          {/* Cycle progress */}
          <div className="mt-6 flex gap-1.5" aria-hidden data-id="element-196" data-mp="22omzj6f7atcm">
            {productFeatures.map((pf, i) => <span key={pf.id} className="h-0.5 flex-1 overflow-hidden bg-white/10" data-id="element-197" data-mp="1aza3bxipl1rn" data-mpi={i}>
                {i === active && <motion.span key={`${pf.id}-${touched}`} className="block h-full bg-marine" initial={{
              width: touched || reduce ? '100%' : '0%'
            }} animate={{
              width: '100%'
            }} transition={{
              duration: touched || reduce ? 0 : CYCLE_MS / 1000,
              ease: 'linear'
            }} data-id="element-198" data-mp="1f03seu7w0mm4" />}
              </span>)}
          </div>
        </div>

        <div className="mt-12 flex justify-center" data-id="element-199" data-mp="2n4vl1q5u2rds">
          <Link to="/m1-pro" className="inline-flex items-center gap-2 font-display text-base font-semibold uppercase tracking-[0.12em] text-white hover:text-ice" data-id="element-200" data-mp="1zdzvz0sriaa8">
            Full M1 PRO specifications <ArrowRightIcon className="h-4 w-4" data-id="element-201" data-mp="28ey3vu6bn1y3" />
          </Link>
        </div>
      </div>
    </section>;
}
function OrbitRings() {
  return <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full" aria-hidden data-id="element-202" data-mp="3js8yikga52v1">
      <g className="mf-spin-slow" data-id="element-203" data-mp="3fr6u7p7ia0m7">
        <circle cx="100" cy="100" r="94" fill="none" stroke="#248DFF" strokeOpacity="0.35" strokeDasharray="1 4" data-id="element-204" data-mp="3fq5ayxgc67te" />
        {Array.from({
        length: 60
      }).map((_, i) => {
        const a = i / 60 * Math.PI * 2;
        const long = i % 5 === 0;
        return <line key={i} x1={100 + Math.cos(a) * (long ? 86 : 89)} y1={100 + Math.sin(a) * (long ? 86 : 89)} x2={100 + Math.cos(a) * 91} y2={100 + Math.sin(a) * 91} stroke="#9CD8FF" strokeOpacity={long ? 0.5 : 0.2} strokeWidth="0.5" data-id="element-205" data-mp="3dcrz40sltdfw" data-mpi={i} />;
      })}
      </g>
      <g className="mf-spin-rev" data-id="element-206" data-mp="602onh755w4">
        <circle cx="100" cy="100" r="80" fill="none" stroke="#9CD8FF" strokeOpacity="0.25" strokeDasharray="30 12 4 12" data-id="element-207" data-mp="4ypaccpk7b8" />
      </g>
    </svg>;
}
function FeatureList({
  items,
  offset,
  active,
  onSelect,
  align
}: {
  items: ProductFeature[];
  offset: number;
  active: number;
  onSelect: (i: number) => void;
  align: 'left' | 'right';
}) {
  return <ul className="hidden space-y-2 lg:block" data-id="element-208" data-mp="2toa7hixg572o">
      {items.map((c, j) => {
      const i = offset + j;
      const on = i === active;
      return <li key={c.id} data-id="element-209" data-mp="2owiq9z06kdp7" data-mpi={j}>
            <button type="button" onClick={() => onSelect(i)} aria-pressed={on} className={`w-full rounded-lg border px-4 py-4 text-left transition-[background-color,border-color] duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice ${align === 'right' ? 'lg:text-right' : ''} ${on ? 'border-marine/60 bg-marine/10' : 'border-transparent hover:border-white/10 hover:bg-white/[0.03]'}`} data-id="element-210" data-mp="2kw89rseri4w3">
              <span className={`block font-display text-2xl font-semibold uppercase tracking-[0.04em] ${on ? 'text-white' : 'text-titanium'}`} data-id="element-211" data-mp="2yo4xlk9edsmo">
                {c.title}
              </span>
              <span className={`mt-1 block text-[15px] ${on ? 'text-titanium' : 'text-titanium/60'}`} data-id="element-212" data-mp="1n8q10hl3p2s3">{c.body}</span>
            </button>
          </li>;
    })}
    </ul>;
}